import { Stack, Typography } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { MAX_INT32_VALUE } from '@tom-ui/utils'
import { ConfigAreaDto, ConfigAreaType, ReeferPlugDirection } from '@yard/app/api'
import { formatBayName, isConfigAreaBlocked } from '@yard/utils/yard-block.util'
import { memo, useState } from 'react'
import { BAY_WIDTH, BayGap, ROW_HEIGHT, edgeAt } from '../../helpers/yard-view.utils'
import { ReeferPlugAreasByBay } from '../../models/yard-view.model'
import { BayGapCell } from './BayGapCell'
import { MaxStackHeight } from './MaxStackHeight'
import { YardAxisContextMenu } from './YardAxisContextMenu'

interface Props {
  maxBays: number
  bayNames: string[]
  isEditable: boolean
  configAreas: ConfigAreaDto[]
  reeferConfigAreaByBay: Record<number, ReeferPlugAreasByBay>
  spaceBetweenBaysByEdge: Record<number, ConfigAreaDto>
  onClick: (bay: number) => void
  onRenameBay: (bay: number) => void
  onInsertBayLeft: (bay: number) => void
  onInsertBayRight: (bay: number) => void
  onDeleteBay: (bay: number) => void
  onBlockConfigArea: (
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
  ) => Promise<void>
  onDeleteConfigArea: (
    configAreaType: ConfigAreaType,
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
    reeferPlugDirection?: ReeferPlugDirection,
  ) => Promise<void>
  onAddReeferPlugs: (bay: number, direction: ReeferPlugDirection) => Promise<void>
  onAddSpaceBetweenBays: (bay: number) => void
  onDeleteSpaceBetweenBays: (bay: number) => Promise<void>
  onAdjustMaxTier: (bay: number) => void
  bayGaps: BayGap[]
  hasLeadingGap?: boolean
  hasTrailingGap?: boolean
  hasTopLeftCornerCell?: boolean
}
export const YardBlockBayHeader = memo(function YardBlockBayHeader(props: Props) {
  const {
    maxBays,
    bayNames,
    isEditable,
    configAreas,
    reeferConfigAreaByBay,
    spaceBetweenBaysByEdge,
    onClick,
    onRenameBay,
    onInsertBayLeft,
    onInsertBayRight,
    onDeleteBay,
    onBlockConfigArea,
    onDeleteConfigArea,
    onAddReeferPlugs,
    onAddSpaceBetweenBays,
    onDeleteSpaceBetweenBays,
    onAdjustMaxTier,
    bayGaps,
    hasLeadingGap,
    hasTrailingGap,
    hasTopLeftCornerCell,
  } = props
  const theme = useMinimalsTheme()

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    bayIndex: number
  } | null>(null)

  const MENU_WIDTH = 250

  const [highlightedBay, setHighlightedBay] = useState<number | null>(null)
  const bayNumber = highlightedBay !== null ? highlightedBay + 1 : null

  const handleBayContextMenu = (e: React.MouseEvent, bayIndex: number) => {
    if (!isEditable) return

    e.preventDefault()
    e.stopPropagation()

    setHighlightedBay(bayIndex)

    const currentElementBoundingRect = e.currentTarget.getBoundingClientRect()
    const bayCenterX = currentElementBoundingRect.left + BAY_WIDTH / 2

    setContextMenu({
      mouseX: bayCenterX - MENU_WIDTH / 2,
      mouseY: currentElementBoundingRect.bottom,
      bayIndex,
    })
  }

  const leadingGap = edgeAt(bayGaps, 0)

  return (
    <Stack flexDirection='row'>
      <YardAxisContextMenu
        axis='bay'
        open={contextMenu !== null}
        width={MENU_WIDTH}
        anchorPosition={
          contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        onClose={() => {
          setContextMenu(null)
          setTimeout(() => {
            setHighlightedBay(null)
          }, 100)
        }}
        onRename={() => {
          if (bayNumber === null) return
          onRenameBay(bayNumber)
        }}
        onInsertBefore={() => {
          if (bayNumber === null) return
          onInsertBayLeft(bayNumber)
        }}
        onInsertAfter={() => {
          if (bayNumber === null) return
          onInsertBayRight(bayNumber)
        }}
        onDelete={() => {
          if (bayNumber === null) return
          onDeleteBay(bayNumber)
        }}
        onBlockConfigArea={
          bayNumber !== null &&
          !isConfigAreaBlocked(configAreas, bayNumber, bayNumber, 1, MAX_INT32_VALUE)
            ? async () => await onBlockConfigArea(bayNumber, bayNumber, 1, MAX_INT32_VALUE)
            : undefined
        }
        onDeleteConfigArea={
          bayNumber !== null &&
          isConfigAreaBlocked(configAreas, bayNumber, bayNumber, 1, MAX_INT32_VALUE)
            ? async configAreaType =>
                await onDeleteConfigArea(configAreaType, bayNumber, bayNumber, 1, MAX_INT32_VALUE)
            : undefined
        }
        onAddForePlugs={
          bayNumber !== null &&
          !reeferConfigAreaByBay[bayNumber]?.fore &&
          !spaceBetweenBaysByEdge[bayNumber - 1]
            ? async () => await onAddReeferPlugs(bayNumber, ReeferPlugDirection.Fore)
            : undefined
        }
        onAddAftPlugs={
          bayNumber !== null &&
          !reeferConfigAreaByBay[bayNumber]?.aft &&
          !spaceBetweenBaysByEdge[bayNumber]
            ? async () => await onAddReeferPlugs(bayNumber, ReeferPlugDirection.Aft)
            : undefined
        }
        onDeleteForePlugs={
          bayNumber !== null && reeferConfigAreaByBay[bayNumber]?.fore
            ? async () => {
                const area = reeferConfigAreaByBay[bayNumber].fore!
                await onDeleteConfigArea(
                  ConfigAreaType.ReeferPlugs,
                  area.minBay,
                  area.maxBay,
                  area.minRow,
                  area.maxRow,
                  ReeferPlugDirection.Fore,
                )
              }
            : undefined
        }
        onDeleteAftPlugs={
          bayNumber !== null && reeferConfigAreaByBay[bayNumber]?.aft
            ? async () => {
                const area = reeferConfigAreaByBay[bayNumber].aft!
                await onDeleteConfigArea(
                  ConfigAreaType.ReeferPlugs,
                  area.minBay,
                  area.maxBay,
                  area.minRow,
                  area.maxRow,
                  ReeferPlugDirection.Aft,
                )
              }
            : undefined
        }
        onAdjustMaxTier={() => {
          if (bayNumber === null) return
          onAdjustMaxTier(bayNumber)
        }}
        onAddSpaceBetweenBays={
          bayNumber !== null &&
          bayNumber < maxBays &&
          !spaceBetweenBaysByEdge[bayNumber] &&
          !reeferConfigAreaByBay[bayNumber]?.aft &&
          !reeferConfigAreaByBay[bayNumber + 1]?.fore
            ? () => onAddSpaceBetweenBays(bayNumber)
            : undefined
        }
        onDeleteSpaceBetweenBays={
          bayNumber !== null && spaceBetweenBaysByEdge[bayNumber]
            ? async () => await onDeleteSpaceBetweenBays(bayNumber)
            : undefined
        }
      />

      {leadingGap && (
        <BayGapCell
          gap={leadingGap}
          variant='outlined'
          highlighted={highlightedBay === 0}
          sx={{
            height: ROW_HEIGHT,
            borderTopLeftRadius: hasTopLeftCornerCell ? 0 : theme.shape.borderRadius,
          }}
        />
      )}

      {Array.from({ length: maxBays }).map((_, bayIndex) => {
        const bayNumberAtIndex = bayIndex + 1
        const isFirst = bayIndex === 0
        const isLast = bayIndex === maxBays - 1
        const trailingGap = edgeAt(bayGaps, bayNumberAtIndex)

        return (
          <Stack key={bayIndex} direction='row' sx={{ flexShrink: 0 }}>
            <Stack
              sx={{
                position: 'relative',
                height: ROW_HEIGHT,
                width: BAY_WIDTH,
                display: 'flex',
                flexDirection: 'row',
                gap: theme.customSpacing.xxxs,
                alignItems: 'center',
                justifyContent: 'center',
                paddingLeft: theme.customSpacing.s,
                paddingRight: theme.customSpacing.s,
                backgroundColor: theme.palette.grey[200],
                border: `1px solid ${theme.palette.grey[300]}`,
                borderTopLeftRadius:
                  isFirst && !hasLeadingGap && !hasTopLeftCornerCell ? theme.shape.borderRadius : 0,
                borderTopRightRadius: isLast && !hasTrailingGap ? theme.shape.borderRadius : 0,
                borderColor:
                  highlightedBay === bayIndex
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
              }}
              onContextMenu={e => handleBayContextMenu(e, bayIndex)}
              onDoubleClick={e => {
                e.stopPropagation()
                onClick(bayNumberAtIndex)
              }}
            >
              <Typography
                variant='subtitle2'
                overflow='hidden'
                textOverflow='ellipsis'
                width='100%'
                whiteSpace='nowrap'
                title={formatBayName(bayNames, bayIndex)}
              >
                {formatBayName(bayNames, bayIndex)}
              </Typography>

              <MaxStackHeight
                configAreas={configAreas}
                minBay={bayNumberAtIndex}
                maxBay={bayNumberAtIndex}
                minRow={1}
                maxRow={MAX_INT32_VALUE}
                sx={{
                  color: theme.palette.grey[500],
                }}
              />
            </Stack>
            {trailingGap && (
              <BayGapCell
                gap={trailingGap}
                variant='outlined'
                highlighted={highlightedBay === bayIndex || highlightedBay === bayIndex + 1}
                sx={{
                  height: ROW_HEIGHT,
                  borderTopRightRadius: isLast && hasTrailingGap ? theme.shape.borderRadius : 0,
                }}
              />
            )}
          </Stack>
        )
      })}
    </Stack>
  )
})
